import { useIntl } from 'react-intl';

/**
 * UTC Date를 원하는 포맷, KST로 변경해준다.
 * @param value 변환할 원본 데이터
 * @param format 원하는 포맷
 */

export const formatMessageTimeStamp = (value: string) => {
  const originDate = new Date(value);

  const isPM = originDate.getHours() > 12;
  const hour = isPM
    ? originDate.getHours() % 12 >= 10
      ? originDate.getHours() % 12
      : '0' + (originDate.getHours() % 12)
    : '' + originDate.getHours();

  const minute =
    ('' + originDate.getMinutes()).length > 1
      ? '' + originDate.getMinutes()
      : '0' + originDate.getMinutes();

  if (isPM) {
    return '오후 ' + hour + ':' + minute;
  }

  return '오전 ' + hour + ':' + minute;
};

/**
 * @param value 변환할 원본 데이터
 * @param format 원하는 포맷
 */
export const formatDate = (
  value: string | number,
  format: string,
  isNonZero?: boolean
): string => {
  const date = new Date(value);
  const year = date.getFullYear();
  let month = '' + (date.getMonth() + 1),
    day = '' + date.getDate(),
    hour = '' + date.getHours(),
    minutes = '' + date.getMinutes(),
    seconds = '' + date.getSeconds();

  if (month.length < 2) month = '0' + month;
  if (day.length < 2) day = '0' + day;
  if (hour.length < 2) hour = isNonZero ? hour : '0' + hour;
  if (minutes.length < 2) minutes = '0' + minutes;
  if (seconds.length < 2) seconds = '0' + seconds;

  const result = format
    .replace('YYYY', String(year))
    .replace('YY', String(year).slice(2, 4))
    .replace('MM', String(month))
    .replace('DD', String(day))
    .replace('HH', String(hour))
    .replace('mm', String(minutes))
    .replace('ss', String(seconds));

  return result;
};

/**
 * 숫자를 통화 포맷으로 변경해준다.
 * @param value 변환할 원본 데이터
 */
export const formatCurrency = (value?: number): string => {
  if (value === 0) return '0';
  if (!value || isNaN(value)) return '';
  return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

/**
 * fileSize를 맞는 단위로 변경해준다.
 * @param value 변환할 원본 데이터
 */
export const formatFileSize = (value?: number): string => {
  if (!value) return '';

  const unit = ['bytes', 'kb', 'mb', 'gb', 'tb', 'pb'];
  const convert = Math.floor(Math.log(value) / Math.log(1024));
  return `${(value / Math.pow(1024, convert)).toFixed(2)} ${unit[convert]}`;
};

/**
 * KTC 채팅 시간 표시 함수
 * @param value
 */
export const timeForTodayKTC = (value: string | number, type?: string) => {
  const today = new Date();
  // UTC -> KST
  const timeValue = new Date(value);

  const betweenTime = Math.floor(
    (today.getTime() - timeValue.getTime()) / 1000 / 60
  );

  const betweenTimeDay = Math.floor(betweenTime / 60 / 24);
  const betweenTimeHour = Math.floor(betweenTime / 60);

  if (type === 'Notification') {
    if (betweenTime < 1) return '방금전';

    if (betweenTime < 60) {
      return `${betweenTime}분전`;
    }

    if (betweenTimeHour < 24) {
      return `${betweenTimeHour}시간전`;
    }

    if (betweenTimeDay < 365) {
      return `${betweenTimeDay}일전`;
    }
  }

  if (betweenTimeDay < 1) return formatDate(value.toString(), 'HH:mm');
  if (betweenTimeDay < 365) return formatDate(value, 'MM월 DD일');
  return formatDate(value, 'YYYY.MM.DD');
};

/**
 * 값을 i18n 언어로 변경함
 * @param id
 */
export const IntlString = (id?: string) => {
  const { formatMessage } = useIntl();

  if (!id) return '';

  const message = formatMessage({ id: id.toUpperCase() });
  if (message === id.toUpperCase()) return '';

  return message;
};

function numberFormat(x: number) {
  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '');
}

export function numberToKorean(number: number) {
  const inputNumber = number < 0 ? 0 : number;
  const unitWords = ['', '만', '억', '조', '경'];
  const splitUnit = 10000;
  const splitCount = unitWords.length;
  const resultArray = [];
  let resultString = '';

  for (let i = 0; i < splitCount; i++) {
    let unitResult =
      (inputNumber % Math.pow(splitUnit, i + 1)) / Math.pow(splitUnit, i);
    unitResult = Math.floor(unitResult);
    if (unitResult > 0) {
      resultArray[i] = unitResult;
    }
  }

  for (let j = 0; j < resultArray.length; j++) {
    if (!resultArray[j]) continue;
    resultString =
      String(numberFormat(resultArray[j])) + unitWords[j] + resultString;
  }

  return resultString;
}

export const priceToKorean = (args: number) => {
  const checkPriceLength = () => {
    if (args.toString().length === 4) {
      return (
        '0.' +
        args
          .toString()
          .substring(0, 1)
          .replace(/\B(?=(\d{4})+(?!\d))/g, ',') +
        '만원'
      );
    }
    if (args.toString().length === 5) {
      return (
        args
          .toString()
          .substring(0, 1)
          .replace(/\B(?=(\d{3})+(?!\d))/g, ',') +
        (+args.toString().substring(1, 2) === 0
          ? ''
          : '.' + +args.toString().substring(1, 2)) +
        '만원'
      );
    }

    if (args.toString().length === 6) {
      return (
        args
          .toString()
          .substring(0, 2)
          .replace(/\B(?=(\d{3})+(?!\d))/g, ',') +
        (+args.toString().substring(2, 3) === 0
          ? ''
          : '.' + +args.toString().substring(2, 3)) +
        '만원'
      );
    }
    if (args.toString().length === 7) {
      return (
        args
          .toString()
          .substring(0, 3)
          .replace(/\B(?=(\d{3})+(?!\d))/g, ',') +
        (+args.toString().substring(3, 4) === 0
          ? ''
          : '.' + +args.toString().substring(3, 4)) +
        '만원'
      );
    }

    if (args.toString().length === 8) {
      return (
        args
          .toString()
          .substring(0, 4)
          .replace(/\B(?=(\d{3})+(?!\d))/g, ',') +
        (+args.toString().substring(4, 5) === 0
          ? ''
          : '.' + +args.toString().substring(4, 5)) +
        '만원'
      );
    }

    if (args.toString().length === 9) {
      return (
        args
          .toString()
          .substring(0, 5)
          .replace(/\B(?=(\d{3})+(?!\d))/g, ',') +
        (+args.toString().substring(5, 6) === 0
          ? ''
          : '.' + +args.toString().substring(5, 6)) +
        '만원'
      );
    }

    if (args.toString().length === 10) {
      return (
        args
          .toString()
          .substring(0, 6)
          .replace(/\B(?=(\d{3})+(?!\d))/g, ',') +
        (+args.toString().substring(6, 7) === 0
          ? ''
          : '.' + +args.toString().substring(6, 7)) +
        '만원'
      );
    }

    return args + '원';
  };

  return checkPriceLength();
};

export function getlastWeek(value: any) {
  //날짜스트링과, 일차를 파라메터로 받는다
  const str = []; //배열
  const b = value.split('-'); //날짜를 - 구분자로 나누어 배열로 변환

  const c = new Date(b[0], b[1] - 1, b[2]); //데이트객체 생성
  const d = c.valueOf() + 1000 * 60 * 60 * 24 * -7; //t일후, (음수면 전일)의 타임스탬프를 얻는다
  const e = new Date(d); //의뢰한날의 데이트객체 생성

  str[str.length] = e.getFullYear(); //년
  str[str.length] = e.getMonth() + 1; //월
  str[str.length] = e.getDate(); //일
  return str.join('-'); //배열을 - 구분자로 합쳐 스트링으로 변환후 반환
}

export const documentDateFormat = (value: string) => {
  const target = new Date(value);

  const year = target.getFullYear();
  const month = target.getMonth() + 1;
  const date = target.getDate();
  const hour = target.getHours();
  const minute = target.getMinutes();

  return `${year}.${month < 10 ? '0' + month : month}.${
    date < 10 ? '0' + date : date
  } ${hour < 10 ? '0' + hour : hour}:${minute < 10 ? '0' + minute : minute}`;
};

export const responseTimeFormatter = (responseTime: string) => {
  const isNan = Number.isNaN;
  let days = '';
  let time = '';
  const timeSplit = responseTime.split('day');

  if (timeSplit.length > 1) {
    days = timeSplit[0];
    if (Number(days) > 1) {
      time = responseTime.split('days')[1];
    } else {
      time = timeSplit[1];
    }
  } else {
    time = timeSplit[0];
  }

  const oneHour = 60 * 60 * 1000;
  const oneDay = 24 * oneHour;
  const day = Number(days.trim());
  const [hour, minute] = time.split(':').map((time) => Number(time));

  if (isNan(day) || isNan(hour) || isNan(minute)) return '';

  const responseTimeToNumber = day * oneDay + hour * oneHour;
  const threeHours = 3 * oneHour;
  const tenHours = 10 * oneHour;
  const threeDays = 3 * oneDay;

  if (responseTimeToNumber < threeHours) {
    return '3시간 이내';
  }

  if (responseTimeToNumber < tenHours) {
    return '10시간 이내';
  }

  if (responseTimeToNumber < oneDay) {
    return '1일 이내';
  }

  if (responseTimeToNumber < threeDays) {
    return '3일 이내';
  }

  return '3일 이상';
};

export const annotationTimeFormatter = (date: string) => {
  const today = new Date();
  const targetDate = new Date(date);

  const betweenTime = Math.floor(
    (today.getTime() - targetDate.getTime()) / 1000 / 60
  );

  const betweenTimeDay = Math.floor(betweenTime / 60 / 24);
  const betweenTimeHour = Math.floor(betweenTime / 60);

  if (betweenTime < 1) return '방금 전';

  if (betweenTime < 60) {
    return `${betweenTime}분 전`;
  }

  if (betweenTimeHour < 24) {
    return `${betweenTimeHour}시간 전`;
  }

  if (betweenTimeDay < 3) {
    return `${betweenTimeDay}일 전`;
  }

  return formatDate(date, 'YYYY MM DD');
};

/**
 * It takes a date and a number of days, and returns a new date that is the number of days away from
 * the original date
 * @param {string | number | Date} base - The base date to calculate from.
 * @param {number} diff - number of days
 * @returns A new Date object
 */
export const dateCalculator = (
  base: string | number | Date,
  diff: number
): Date | undefined => {
  let baseDate;

  if (typeof base === 'object') {
    baseDate = base;
  } else {
    baseDate = new Date(base);
  }

  if (isNaN(baseDate.getTime())) {
    return;
  }

  const oneDay = 24 * 60 * 60 * 1000;
  const diffDate = baseDate.getTime() + oneDay * diff;

  return new Date(diffDate);
};
